// Demo transactions for testing cancel and amend flows
// Based on the transaction rules specification

export interface DemoTransaction {
  id: string
  mtoName: string
  mtoRef: string
  bankRef: string
  branchCode: string
  agentCode: string
  payoutType: 'FT' | 'IBFT' | 'OTC'
  amountFCY: string
  currencyFCY: string
  amountPKR: string
  corridor: string
  purposeCode: string
  purposeText: string
  currentState: string
  lifecycleStatus: string
  createdAt: string
  updatedAt: string
  remitterName: string
  beneficiaryName: string
  beneficiaryIBAN: string
  beneficiaryBankName?: string
  amendAllowed: boolean
  cancelAllowed: boolean
  cancellationAction: string
  amendStatus: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED'
  cancelStatus: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED'
  railRefs: any
  stateHistory: any[]
  ePRCNo?: string
  ePRCDate?: string
  amendmentHistory?: {
    timestamp: string
    oldAccount: string
    newAccount: string
    fetchedTitle: string
    status: 'PENDING' | 'APPROVED' | 'REJECTED'
    requestedBy: string
    checkerComment?: string
  }[]
  manualActionComment?: {
    actionType: 'MANUAL_FAIL' | 'MANUAL_COMPLETE'
    comment: string
    timestamp: string
  }
}

const VALID_MTOS = [
  'RIA Money Transfer',
  'InstantCash',
  'MoneyGram',
  'Western Union',
]
const VALID_SUB_AGENTS = ['SA001', 'SA002', 'SA003', 'SA004']

export const DEMO_TRANSACTIONS: DemoTransaction[] = [
  // OTC Transaction - DOWNLOAD (Failed/Inprogress) - Amend Yes, Cancel Yes
  {
    id: 'TXN-DEMO-001',
    mtoName: 'RIA Money Transfer',
    mtoRef: 'MTO-DEMO-001',
    bankRef: 'BNK-DEMO-001',
    branchCode: '0001',
    agentCode: 'SA001',
    payoutType: 'OTC',
    amountFCY: '500.00',
    currencyFCY: 'USD',
    amountPKR: '140,000',
    corridor: 'PK-US',
    purposeCode: 'FAM',
    purposeText: 'Family Support',
    currentState: 'DOWNLOAD',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 7200000).toISOString(),
    updatedAt: new Date(Date.now() - 3600000).toISOString(),
    remitterName: 'John Doe',
    beneficiaryName: 'Jane Doe',
    beneficiaryIBAN: 'PK00000000000000000001',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // OTC Transaction - SCREENING (Inprogress) - Amend Yes, Cancel Yes
  {
    id: 'TXN-DEMO-002',
    mtoName: 'InstantCash',
    mtoRef: 'MTO-DEMO-002',
    bankRef: 'BNK-DEMO-002',
    branchCode: '0002',
    agentCode: 'SA002',
    payoutType: 'OTC',
    amountFCY: '750.00',
    currencyFCY: 'USD',
    amountPKR: '210,000',
    corridor: 'PK-AE',
    purposeCode: 'SAL',
    purposeText: 'Salary',
    currentState: 'SCREENING',
    lifecycleStatus: 'IN_PROGRESS',
    createdAt: new Date(Date.now() - 5400000).toISOString(),
    updatedAt: new Date(Date.now() - 1800000).toISOString(),
    remitterName: 'Ahmed Ali',
    beneficiaryName: 'Sara Ahmed',
    beneficiaryIBAN: 'PK00000000000000000002',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // OTC Transaction - TITLE_FETCH (Failed) - Amend Yes, Cancel Yes
  {
    id: 'TXN-DEMO-003',
    mtoName: 'MoneyGram',
    mtoRef: 'MTO-DEMO-003',
    bankRef: 'BNK-DEMO-003',
    branchCode: '0003',
    agentCode: 'SA003',
    payoutType: 'OTC',
    amountFCY: '1000.00',
    currencyFCY: 'USD',
    amountPKR: '280,000',
    corridor: 'PK-UK',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 10800000).toISOString(),
    updatedAt: new Date(Date.now() - 7200000).toISOString(),
    remitterName: 'Michael Brown',
    beneficiaryName: 'Ali Hassan',
    beneficiaryIBAN: 'PK00000000000000000003',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'PENDING',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // LFT Transaction - DOWNLOAD (Failed) - Amend Yes, Cancel Yes
  {
    id: 'TXN-DEMO-004',
    mtoName: 'Western Union',
    mtoRef: 'MTO-DEMO-004',
    bankRef: 'BNK-DEMO-004',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '600.00',
    currencyFCY: 'USD',
    amountPKR: '168,000',
    corridor: 'PK-SA',
    purposeCode: 'MED',
    purposeText: 'Medical',
    currentState: 'DOWNLOAD',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 14400000).toISOString(),
    updatedAt: new Date(Date.now() - 10800000).toISOString(),
    remitterName: 'Fatima Khan',
    beneficiaryName: 'Zainab Ali',
    beneficiaryIBAN: 'PK00000000000000000004',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // LFT Transaction - BALANCE_INQUIRY (Inprogress) - Amend Yes, Cancel Yes
  {
    id: 'TXN-DEMO-005',
    mtoName: 'RIA Money Transfer',
    mtoRef: 'MTO-DEMO-005',
    bankRef: 'BNK-DEMO-005',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '850.00',
    currencyFCY: 'USD',
    amountPKR: '238,000',
    corridor: 'PK-US',
    purposeCode: 'FAM',
    purposeText: 'Family Support',
    currentState: 'BALANCE_CHECK',
    lifecycleStatus: 'IN_PROGRESS',
    createdAt: new Date(Date.now() - 3600000).toISOString(),
    updatedAt: new Date(Date.now() - 1800000).toISOString(),
    remitterName: 'David Smith',
    beneficiaryName: 'Omar Farooq',
    beneficiaryIBAN: 'PK00000000000000000005',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'APPROVED',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // Payment (internal FT) - Failed - Amend No, Cancel Yes
  {
    id: 'TXN-DEMO-006',
    mtoName: 'InstantCash',
    mtoRef: 'MTO-DEMO-006',
    bankRef: 'BNK-DEMO-006',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '1200.00',
    currencyFCY: 'USD',
    amountPKR: '336,000',
    corridor: 'PK-AE',
    purposeCode: 'SAL',
    purposeText: 'Salary',
    currentState: 'FT_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 18000000).toISOString(),
    updatedAt: new Date(Date.now() - 14400000).toISOString(),
    remitterName: 'Hassan Mahmood',
    beneficiaryName: 'Ayesha Khan',
    beneficiaryIBAN: 'PK00000000000000000006',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // IBFT Transaction - DOWNLOAD (Failed) - Amend Yes, Cancel Yes
  {
    id: 'TXN-DEMO-007',
    mtoName: 'MoneyGram',
    mtoRef: 'MTO-DEMO-007',
    bankRef: 'BNK-DEMO-007',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '950.00',
    currencyFCY: 'USD',
    amountPKR: '266,000',
    corridor: 'PK-UK',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'DOWNLOAD',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 21600000).toISOString(),
    updatedAt: new Date(Date.now() - 18000000).toISOString(),
    remitterName: 'Emma Wilson',
    beneficiaryName: 'Bilal Ahmed',
    beneficiaryIBAN: 'PK00000000000000000007',
    beneficiaryBankName: 'Meezan Bank',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'PENDING',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // IBFT Transaction - BALANCE_INQUIRY (Inprogress) - Amend Yes, Cancel Yes
  {
    id: 'TXN-DEMO-008',
    mtoName: 'Western Union',
    mtoRef: 'MTO-DEMO-008',
    bankRef: 'BNK-DEMO-008',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '700.00',
    currencyFCY: 'USD',
    amountPKR: '196,000',
    corridor: 'PK-SA',
    purposeCode: 'MED',
    purposeText: 'Medical',
    currentState: 'BALANCE_CHECK',
    lifecycleStatus: 'IN_PROGRESS',
    createdAt: new Date(Date.now() - 7200000).toISOString(),
    updatedAt: new Date(Date.now() - 3600000).toISOString(),
    remitterName: 'Sophia Lee',
    beneficiaryName: 'Usman Malik',
    beneficiaryIBAN: 'PK00000000000000000008',
    beneficiaryBankName: 'HBL',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'REJECTED',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // IBFT Transaction - PAYMENT (RAAST/1LINK) Failed - Amend No, Cancel Yes
  {
    id: 'TXN-DEMO-009',
    mtoName: 'RIA Money Transfer',
    mtoRef: 'MTO-DEMO-009',
    bankRef: 'BNK-DEMO-009',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '1100.00',
    currencyFCY: 'USD',
    amountPKR: '308,000',
    corridor: 'PK-US',
    purposeCode: 'FAM',
    purposeText: 'Family Support',
    currentState: 'RAAST_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 25200000).toISOString(),
    updatedAt: new Date(Date.now() - 21600000).toISOString(),
    remitterName: 'James Taylor',
    beneficiaryName: 'Hina Tariq',
    beneficiaryIBAN: 'PK00000000000000000009',
    beneficiaryBankName: 'UBL',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'APPROVED',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // RTGS Transaction - Failed - Amend No, Cancel Yes
  {
    id: 'TXN-DEMO-010',
    mtoName: 'InstantCash',
    mtoRef: 'MTO-DEMO-010',
    bankRef: 'BNK-DEMO-010',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '2000.00',
    currencyFCY: 'USD',
    amountPKR: '560,000',
    corridor: 'PK-AE',
    purposeCode: 'SAL',
    purposeText: 'Salary',
    currentState: 'RTGS_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 28800000).toISOString(),
    updatedAt: new Date(Date.now() - 25200000).toISOString(),
    remitterName: 'Mohammed Rashid',
    beneficiaryName: 'Sana Iqbal',
    beneficiaryIBAN: 'PK00000000000000000010',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'REVERSE_FINANCIAL_ACTION',
    amendStatus: 'NONE',
    cancelStatus: 'REJECTED',
    railRefs: {},
    stateHistory: [],
    ePRCNo: undefined,
    ePRCDate: undefined,
  },
  // SUCCESS Transaction - PAID - Amend No, Cancel No
  {
    id: 'TXN-DEMO-011',
    mtoName: 'MoneyGram',
    mtoRef: 'MTO-DEMO-011',
    bankRef: 'BNK-DEMO-011',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '800.00',
    currencyFCY: 'USD',
    amountPKR: '224,000',
    corridor: 'PK-UK',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'SUCCESS',
    lifecycleStatus: 'SUCCESS',
    createdAt: new Date(Date.now() - 32400000).toISOString(),
    updatedAt: new Date(Date.now() - 28800000).toISOString(),
    remitterName: 'Oliver Brown',
    beneficiaryName: 'Kamran Shah',
    beneficiaryIBAN: 'PK00000000000000000011',
    amendAllowed: false,
    cancelAllowed: false,
    cancellationAction: 'CANNOT_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {
      RRN: 'RRN000000000011',
      STAN: '000011',
    },
    stateHistory: [],
    ePRCNo: 'EPRC-DEMO-011',
    ePRCDate: new Date(Date.now() - 28800000).toISOString(),
  },

  // NEW: FT Transaction - TITLE_FETCH (Failed) - Amend Yes
  {
    id: 'TXN-DEMO-012',
    mtoName: 'RIA Money Transfer',
    mtoRef: 'MTO-DEMO-012',
    bankRef: 'BNK-DEMO-012',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '1500.00',
    currencyFCY: 'USD',
    amountPKR: '420,000',
    corridor: 'PK-US',
    purposeCode: 'FAM',
    purposeText: 'Family Support',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 3600000).toISOString(),
    updatedAt: new Date(Date.now() - 1800000).toISOString(),
    remitterName: 'Alice Johnson',
    beneficiaryName: 'Unknown Title',
    beneficiaryIBAN: 'PK12345678901234567890',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 1800000).toISOString(),
        primaryRef: 'TITLE_FETCH-012',
        attempts: 1,
        failReasonCode: 'INVALID_ACCOUNT',
      },
    ],
  },
  // NEW: IBFT Transaction - TITLE_FETCH (Failed) - Amend Yes with History
  {
    id: 'TXN-DEMO-013',
    mtoName: 'InstantCash',
    mtoRef: 'MTO-DEMO-013',
    bankRef: 'BNK-DEMO-013',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '250.00',
    currencyFCY: 'USD',
    amountPKR: '70,000',
    corridor: 'PK-AE',
    purposeCode: 'SAL',
    purposeText: 'Salary',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 86400000).toISOString(),
    updatedAt: new Date(Date.now() - 43200000).toISOString(),
    remitterName: 'Bob Smith',
    beneficiaryName: 'Incorrect Name',
    beneficiaryIBAN: 'PK09876543210987654321',
    beneficiaryBankName: 'Allied Bank',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'REJECTED',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 43200000).toISOString(),
        primaryRef: 'TITLE_FETCH-013',
        attempts: 2,
        failReasonCode: 'TITLE_MISMATCH',
      },
    ],
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 60000000).toISOString(),
        oldAccount: 'PK09876543210987654321',
        newAccount: 'PK11112222333344445555',
        fetchedTitle: 'BOB SMITH',
        status: 'REJECTED',
        requestedBy: 'Maker User 1',
        checkerComment: 'Title does not match beneficiary name exactly.',
      },
    ],
  },
  // NEW: FT Transaction - RTGS_POST (Failed) - Manual Action Comment
  {
    id: 'TXN-DEMO-014',
    mtoName: 'Western Union',
    mtoRef: 'MTO-DEMO-014',
    bankRef: 'BNK-DEMO-014',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '5000.00',
    currencyFCY: 'USD',
    amountPKR: '1,400,000',
    corridor: 'PK-UK',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'RTGS_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 172800000).toISOString(),
    updatedAt: new Date(Date.now() - 86400000).toISOString(),
    remitterName: 'Charlie Brown',
    beneficiaryName: 'University of London',
    beneficiaryIBAN: 'PK55556666777788889999',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'REVERSE_FINANCIAL_ACTION',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'RTGS_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 86400000).toISOString(),
        primaryRef: 'RTGS_POST-014',
        attempts: 1,
        failReasonCode: 'SYSTEM_TIMEOUT',
      },
    ],
    manualActionComment: {
      actionType: 'MANUAL_FAIL',
      comment:
        'RTGS system was down during processing window. Marking as failed manually.',
      timestamp: new Date(Date.now() - 80000000).toISOString(),
    },
  },

  // ===== NEW DEMO TRANSACTIONS FOR AMENDMENT HISTORY & RTGS COMMENTS =====

  // TXN-DEMO-015: FT - SUCCESS after amendment (approved) - shows old/new values in txn screen
  {
    id: 'TXN-DEMO-015',
    mtoName: 'RIA Money Transfer',
    mtoRef: 'MTO-DEMO-015',
    bankRef: 'BNK-DEMO-015',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '2500.00',
    currencyFCY: 'USD',
    amountPKR: '700,000',
    corridor: 'PK-US',
    purposeCode: 'FAM',
    purposeText: 'Family Support',
    currentState: 'SUCCESS',
    lifecycleStatus: 'SUCCESS',
    createdAt: new Date(Date.now() - 259200000).toISOString(),
    updatedAt: new Date(Date.now() - 172800000).toISOString(),
    remitterName: 'Robert Williams',
    beneficiaryName: 'MUHAMMAD ALI KHAN',
    beneficiaryIBAN: 'PK36MEZN0099012345678',
    amendAllowed: false,
    cancelAllowed: false,
    cancellationAction: 'CANNOT_CANCEL',
    amendStatus: 'APPROVED',
    cancelStatus: 'NONE',
    railRefs: {
      RRN: 'RRN000000000015',
      STAN: '000015',
    },
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 250000000).toISOString(),
        primaryRef: 'SCR-015',
        durationMs: 1100,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 248000000).toISOString(),
        primaryRef: 'TF-015-FAIL',
        failReasonCode: 'INVALID_ACCOUNT',
        durationMs: 900,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 200000000).toISOString(),
        primaryRef: 'TF-015-PASS',
        durationMs: 750,
        attempts: 1,
      },
      {
        stateCode: 'BALANCE_CHECK',
        status: 'PASS',
        timestamp: new Date(Date.now() - 198000000).toISOString(),
        primaryRef: 'BC-015',
        durationMs: 400,
        attempts: 1,
      },
      {
        stateCode: 'FT_POST',
        status: 'PASS',
        timestamp: new Date(Date.now() - 195000000).toISOString(),
        primaryRef: 'RRN000000000015',
        durationMs: 2100,
        attempts: 1,
      },
      {
        stateCode: 'SUCCESS',
        status: 'PASS',
        timestamp: new Date(Date.now() - 172800000).toISOString(),
        primaryRef: 'EPRC-015',
        durationMs: 80,
        attempts: 1,
      },
    ],
    ePRCNo: 'EPRC-DEMO-015',
    ePRCDate: new Date(Date.now() - 172800000).toISOString(),
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 240000000).toISOString(),
        oldAccount: 'PK12ABCD0000000000000',
        newAccount: 'PK36MEZN0099012345678',
        fetchedTitle: 'MUHAMMAD ALI KHAN',
        status: 'APPROVED',
        requestedBy: 'admin',
        checkerComment: 'Title verified and matches beneficiary. Approved.',
      },
    ],
  },

  // TXN-DEMO-016: IBFT - SUCCESS after TWO amendments (1 rejected, 1 approved)
  {
    id: 'TXN-DEMO-016',
    mtoName: 'MoneyGram',
    mtoRef: 'MTO-DEMO-016',
    bankRef: 'BNK-DEMO-016',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '1800.00',
    currencyFCY: 'USD',
    amountPKR: '504,000',
    corridor: 'PK-UK',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'SUCCESS',
    lifecycleStatus: 'SUCCESS',
    createdAt: new Date(Date.now() - 345600000).toISOString(),
    updatedAt: new Date(Date.now() - 259200000).toISOString(),
    remitterName: 'Sarah Johnson',
    beneficiaryName: 'FATIMA BIBI',
    beneficiaryIBAN: 'PK50HABB0012345678901',
    beneficiaryBankName: 'Bank Alfalah',
    amendAllowed: false,
    cancelAllowed: false,
    cancellationAction: 'CANNOT_CANCEL',
    amendStatus: 'APPROVED',
    cancelStatus: 'NONE',
    railRefs: {
      RaastMsgId: 'RAAST-MSG-016',
      UETR: 'UETR-016',
    },
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 340000000).toISOString(),
        primaryRef: 'SCR-016',
        durationMs: 1300,
        attempts: 1,
      },
      {
        stateCode: 'RAAST_TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 338000000).toISOString(),
        primaryRef: 'RAAST-TF-016-FAIL',
        failReasonCode: 'TITLE_MISMATCH',
        durationMs: 1100,
        attempts: 2,
      },
      {
        stateCode: 'RAAST_TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 280000000).toISOString(),
        primaryRef: 'RAAST-TF-016-PASS',
        durationMs: 900,
        attempts: 1,
      },
      {
        stateCode: 'RAAST_POST',
        status: 'PASS',
        timestamp: new Date(Date.now() - 275000000).toISOString(),
        primaryRef: 'RAAST-MSG-016',
        durationMs: 2800,
        attempts: 1,
      },
      {
        stateCode: 'SUCCESS',
        status: 'PASS',
        timestamp: new Date(Date.now() - 259200000).toISOString(),
        primaryRef: 'EPRC-016',
        durationMs: 90,
        attempts: 1,
      },
    ],
    ePRCNo: 'EPRC-DEMO-016',
    ePRCDate: new Date(Date.now() - 259200000).toISOString(),
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 330000000).toISOString(),
        oldAccount: 'PK99XYZZ9999999999999',
        newAccount: 'PK22ABCD1111222233334',
        fetchedTitle: 'FATIMA B',
        status: 'REJECTED',
        requestedBy: 'admin',
        checkerComment:
          'Title is truncated. Cannot verify identity. Please retry with correct account.',
      },
      {
        timestamp: new Date(Date.now() - 300000000).toISOString(),
        oldAccount: 'PK99XYZZ9999999999999',
        newAccount: 'PK50HABB0012345678901',
        fetchedTitle: 'FATIMA BIBI',
        status: 'APPROVED',
        requestedBy: 'admin',
        checkerComment: 'Title matches. Account verified successfully.',
      },
    ],
  },

  // TXN-DEMO-017: FT - RTGS manually marked as COMPLETED (success) with comment
  {
    id: 'TXN-DEMO-017',
    mtoName: 'Western Union',
    mtoRef: 'MTO-DEMO-017',
    bankRef: 'BNK-DEMO-017',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '10000.00',
    currencyFCY: 'USD',
    amountPKR: '2,800,000',
    corridor: 'PK-SA',
    purposeCode: 'SAL',
    purposeText: 'Salary',
    currentState: 'RTGS_POST',
    lifecycleStatus: 'SUCCESS',
    createdAt: new Date(Date.now() - 432000000).toISOString(),
    updatedAt: new Date(Date.now() - 345600000).toISOString(),
    remitterName: 'Abdullah Al-Rashid',
    beneficiaryName: 'Karachi Steel Works Ltd',
    beneficiaryIBAN: 'PK77NBPA0000001234567',
    amendAllowed: false,
    cancelAllowed: false,
    cancellationAction: 'CANNOT_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {
      UETR: 'UETR-RTGS-017',
    },
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 430000000).toISOString(),
        primaryRef: 'SCR-017',
        durationMs: 1400,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 428000000).toISOString(),
        primaryRef: 'TF-017',
        durationMs: 850,
        attempts: 1,
      },
      {
        stateCode: 'RTGS_BALANCE_CHECK',
        status: 'PASS',
        timestamp: new Date(Date.now() - 426000000).toISOString(),
        primaryRef: 'RTGS-BC-017',
        durationMs: 600,
        attempts: 1,
      },
      {
        stateCode: 'RTGS_POST',
        status: 'PASS',
        timestamp: new Date(Date.now() - 345600000).toISOString(),
        primaryRef: 'RTGS-017',
        durationMs: 5000,
        attempts: 1,
      },
    ],
    ePRCNo: 'EPRC-DEMO-017',
    ePRCDate: new Date(Date.now() - 345600000).toISOString(),
    manualActionComment: {
      actionType: 'MANUAL_COMPLETE',
      comment:
        'Confirmed with RTGS operations team. Funds credited to beneficiary account PK77NBPA0000001234567. Bank confirmation ref: RTGS-CONF-2025-0417.',
      timestamp: new Date(Date.now() - 345600000).toISOString(),
    },
  },

  // TXN-DEMO-018: FT - RTGS manually marked as FAILED with comment
  {
    id: 'TXN-DEMO-018',
    mtoName: 'InstantCash',
    mtoRef: 'MTO-DEMO-018',
    bankRef: 'BNK-DEMO-018',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '7500.00',
    currencyFCY: 'USD',
    amountPKR: '2,100,000',
    corridor: 'PK-AE',
    purposeCode: 'MED',
    purposeText: 'Medical',
    currentState: 'RTGS_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 518400000).toISOString(),
    updatedAt: new Date(Date.now() - 432000000).toISOString(),
    remitterName: 'Khalid Ibrahim',
    beneficiaryName: 'Aga Khan Hospital',
    beneficiaryIBAN: 'PK88SCBL0000009876543',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'REVERSE_FINANCIAL_ACTION',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {
      UETR: 'UETR-RTGS-018',
    },
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 516000000).toISOString(),
        primaryRef: 'SCR-018',
        durationMs: 1200,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 514000000).toISOString(),
        primaryRef: 'TF-018',
        durationMs: 900,
        attempts: 1,
      },
      {
        stateCode: 'RTGS_BALANCE_CHECK',
        status: 'PASS',
        timestamp: new Date(Date.now() - 512000000).toISOString(),
        primaryRef: 'RTGS-BC-018',
        durationMs: 550,
        attempts: 1,
      },
      {
        stateCode: 'RTGS_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 432000000).toISOString(),
        primaryRef: 'RTGS-018',
        failReasonCode: 'BENEFICIARY_BANK_REJECTED',
        durationMs: 8000,
        attempts: 2,
      },
    ],
    manualActionComment: {
      actionType: 'MANUAL_FAIL',
      comment:
        'Beneficiary bank (SCB) rejected the RTGS transfer. Reason: Account frozen due to regulatory hold. Contacted beneficiary bank ops desk - confirmed account is under investigation. Transaction cannot proceed.',
      timestamp: new Date(Date.now() - 430000000).toISOString(),
    },
  },

  // TXN-DEMO-019: IBFT - Amendment approved + manual complete comment (full journey)
  {
    id: 'TXN-DEMO-019',
    mtoName: 'RIA Money Transfer',
    mtoRef: 'MTO-DEMO-019',
    bankRef: 'BNK-DEMO-019',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '3200.00',
    currencyFCY: 'USD',
    amountPKR: '896,000',
    corridor: 'PK-US',
    purposeCode: 'FAM',
    purposeText: 'Family Support',
    currentState: 'SUCCESS',
    lifecycleStatus: 'SUCCESS',
    createdAt: new Date(Date.now() - 604800000).toISOString(),
    updatedAt: new Date(Date.now() - 518400000).toISOString(),
    remitterName: 'Maria Garcia',
    beneficiaryName: 'AHMED RAZA SHEIKH',
    beneficiaryIBAN: 'PK44UNIL0109000123456',
    beneficiaryBankName: 'MCB Bank',
    amendAllowed: false,
    cancelAllowed: false,
    cancellationAction: 'CANNOT_CANCEL',
    amendStatus: 'APPROVED',
    cancelStatus: 'NONE',
    railRefs: {
      RaastMsgId: 'RAAST-MSG-019',
      UETR: 'UETR-019',
    },
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 600000000).toISOString(),
        primaryRef: 'SCR-019',
        durationMs: 1100,
        attempts: 1,
      },
      {
        stateCode: 'RAAST_TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 598000000).toISOString(),
        primaryRef: 'RAAST-TF-019-FAIL',
        failReasonCode: 'ACCOUNT_CLOSED',
        durationMs: 1200,
        attempts: 1,
      },
      {
        stateCode: 'RAAST_TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 540000000).toISOString(),
        primaryRef: 'RAAST-TF-019-PASS',
        durationMs: 800,
        attempts: 1,
      },
      {
        stateCode: 'RAAST_POST',
        status: 'PASS',
        timestamp: new Date(Date.now() - 535000000).toISOString(),
        primaryRef: 'RAAST-MSG-019',
        durationMs: 3100,
        attempts: 1,
      },
      {
        stateCode: 'SUCCESS',
        status: 'PASS',
        timestamp: new Date(Date.now() - 518400000).toISOString(),
        primaryRef: 'EPRC-019',
        durationMs: 100,
        attempts: 1,
      },
    ],
    ePRCNo: 'EPRC-DEMO-019',
    ePRCDate: new Date(Date.now() - 518400000).toISOString(),
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 580000000).toISOString(),
        oldAccount: 'PK33CLOSED000000000000',
        newAccount: 'PK44UNIL0109000123456',
        fetchedTitle: 'AHMED RAZA SHEIKH',
        status: 'APPROVED',
        requestedBy: 'admin',
        checkerComment:
          'Original account was closed. New account title matches beneficiary. Approved for reprocessing.',
      },
    ],
    manualActionComment: {
      actionType: 'MANUAL_COMPLETE',
      comment:
        'Transaction reprocessed after account amendment. Raast confirmation received. Funds successfully credited.',
      timestamp: new Date(Date.now() - 520000000).toISOString(),
    },
  },

  // ===== CHECKER INBOX AMENDMENT TRANSACTIONS =====
  // These transactions appear in both the main Transactions table AND the Checker Amendment Inbox

  {
    id: 'TXN-30000',
    mtoName: 'RIA Money Transfer',
    mtoRef: 'MTO-30000',
    bankRef: 'BNK-30000',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '1000.00',
    currencyFCY: 'USD',
    amountPKR: '280,000',
    corridor: 'PK-US',
    purposeCode: 'FAM',
    purposeText: 'Family Support',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 7200000).toISOString(),
    updatedAt: new Date(Date.now() - 3600000).toISOString(),
    remitterName: 'Remitter 1',
    beneficiaryName: 'MUHAMMAD ALI',
    beneficiaryIBAN: 'PK0000000000000000000000',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'PENDING',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 6000000).toISOString(),
        primaryRef: 'SCR-30000',
        durationMs: 1200,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 3600000).toISOString(),
        primaryRef: 'TF-30000',
        failReasonCode: 'INVALID_ACCOUNT',
        durationMs: 900,
        attempts: 2,
      },
    ],
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 3600000).toISOString(),
        oldAccount: 'PK0000000000000000000000',
        newAccount: 'PK0000000000000000000199',
        fetchedTitle: 'MUHAMMAD ALI',
        status: 'PENDING',
        requestedBy: 'Admin User',
      },
    ],
  },

  {
    id: 'TXN-30001',
    mtoName: 'InstantCash',
    mtoRef: 'MTO-30001',
    bankRef: 'BNK-30001',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '1100.00',
    currencyFCY: 'USD',
    amountPKR: '308,000',
    corridor: 'PK-AE',
    purposeCode: 'SAL',
    purposeText: 'Salary',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 10800000).toISOString(),
    updatedAt: new Date(Date.now() - 7200000).toISOString(),
    remitterName: 'Remitter 2',
    beneficiaryName: 'FATIMA KHAN',
    beneficiaryIBAN: 'PK0000000000000000000001',
    beneficiaryBankName: 'Faysal Bank',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'PENDING',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 9000000).toISOString(),
        primaryRef: 'SCR-30001',
        durationMs: 1100,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 7200000).toISOString(),
        primaryRef: 'TF-30001',
        failReasonCode: 'TITLE_MISMATCH',
        durationMs: 800,
        attempts: 1,
      },
    ],
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 7200000).toISOString(),
        oldAccount: 'PK0000000000000000000001',
        newAccount: 'PK0000000000000000000200',
        fetchedTitle: 'FATIMA KHAN',
        status: 'PENDING',
        requestedBy: 'Maker User 1',
      },
    ],
  },

  {
    id: 'TXN-30002',
    mtoName: 'MoneyGram',
    mtoRef: 'MTO-30002',
    bankRef: 'BNK-30002',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '1200.00',
    currencyFCY: 'USD',
    amountPKR: '336,000',
    corridor: 'PK-UK',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 14400000).toISOString(),
    updatedAt: new Date(Date.now() - 10800000).toISOString(),
    remitterName: 'Remitter 3',
    beneficiaryName: 'USMAN TARIQ',
    beneficiaryIBAN: 'PK0000000000000000000002',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'PENDING',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 13000000).toISOString(),
        primaryRef: 'SCR-30002',
        durationMs: 1300,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 10800000).toISOString(),
        primaryRef: 'TF-30002',
        failReasonCode: 'INVALID_ACCOUNT',
        durationMs: 1000,
        attempts: 3,
      },
    ],
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 10800000).toISOString(),
        oldAccount: 'PK0000000000000000000002',
        newAccount: 'PK0000000000000000000201',
        fetchedTitle: 'USMAN TARIQ',
        status: 'PENDING',
        requestedBy: 'Maker User 2',
      },
    ],
  },

  {
    id: 'TXN-30003',
    mtoName: 'RIA Money Transfer',
    mtoRef: 'MTO-30003',
    bankRef: 'BNK-30003',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '1300.00',
    currencyFCY: 'USD',
    amountPKR: '364,000',
    corridor: 'PK-SA',
    purposeCode: 'MED',
    purposeText: 'Medical',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 18000000).toISOString(),
    updatedAt: new Date(Date.now() - 14400000).toISOString(),
    remitterName: 'Remitter 4',
    beneficiaryName: 'MUHAMMAD ALI',
    beneficiaryIBAN: 'PK0000000000000000000003',
    beneficiaryBankName: 'Askari Bank',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'PENDING',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 17000000).toISOString(),
        primaryRef: 'SCR-30003',
        durationMs: 1000,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 14400000).toISOString(),
        primaryRef: 'TF-30003',
        failReasonCode: 'ACCOUNT_DORMANT',
        durationMs: 750,
        attempts: 1,
      },
    ],
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 14400000).toISOString(),
        oldAccount: 'PK0000000000000000000003',
        newAccount: 'PK0000000000000000000202',
        fetchedTitle: 'MUHAMMAD ALI',
        status: 'PENDING',
        requestedBy: 'Admin User',
      },
    ],
  },

  {
    id: 'TXN-30004',
    mtoName: 'InstantCash',
    mtoRef: 'MTO-30004',
    bankRef: 'BNK-30004',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '1400.00',
    currencyFCY: 'USD',
    amountPKR: '392,000',
    corridor: 'PK-US',
    purposeCode: 'FAM',
    purposeText: 'Family Support',
    currentState: 'TITLE_FETCH',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 21600000).toISOString(),
    updatedAt: new Date(Date.now() - 18000000).toISOString(),
    remitterName: 'Remitter 5',
    beneficiaryName: 'FATIMA KHAN',
    beneficiaryIBAN: 'PK0000000000000000000004',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'PENDING',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 20000000).toISOString(),
        primaryRef: 'SCR-30004',
        durationMs: 1200,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 18000000).toISOString(),
        primaryRef: 'TF-30004',
        failReasonCode: 'INVALID_ACCOUNT',
        durationMs: 850,
        attempts: 2,
      },
    ],
    amendmentHistory: [
      {
        timestamp: new Date(Date.now() - 18000000).toISOString(),
        oldAccount: 'PK0000000000000000000004',
        newAccount: 'PK0000000000000000000203',
        fetchedTitle: 'FATIMA KHAN',
        status: 'PENDING',
        requestedBy: 'Maker User 2',
      },
    ],
  },

  // ===== 1LINK TIMEOUT TRANSACTIONS =====
  {
    id: 'TXN-DEMO-030',
    mtoName: 'MoneyGram',
    mtoRef: 'MTO-DEMO-030',
    bankRef: 'BNK-DEMO-030',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '1350.00',
    currencyFCY: 'USD',
    amountPKR: '378,000',
    corridor: 'PK-UK',
    purposeCode: 'FAM',
    purposeText: 'Family Support',
    currentState: 'ONELINK_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 4800000).toISOString(),
    updatedAt: new Date(Date.now() - 2400000).toISOString(),
    remitterName: 'William Clarke',
    beneficiaryName: 'Imran Hussain',
    beneficiaryIBAN: 'PK22ALFH0099001234567',
    beneficiaryBankName: 'Bank Alfalah',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 4500000).toISOString(),
        primaryRef: 'SCR-DEMO-030',
        durationMs: 1100,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 4200000).toISOString(),
        primaryRef: 'TF-DEMO-030',
        durationMs: 800,
        attempts: 1,
      },
      {
        stateCode: 'ONELINK_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 2400000).toISOString(),
        primaryRef: '1LINK-DEMO-030',
        failReasonCode: 'ONELINK_TIMEOUT',
        durationMs: 30000,
        attempts: 2,
      },
    ],
  },
  {
    id: 'TXN-DEMO-031',
    mtoName: 'Western Union',
    mtoRef: 'MTO-DEMO-031',
    bankRef: 'BNK-DEMO-031',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '890.00',
    currencyFCY: 'USD',
    amountPKR: '249,200',
    corridor: 'PK-SA',
    purposeCode: 'SAL',
    purposeText: 'Salary',
    currentState: 'ONELINK_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 9600000).toISOString(),
    updatedAt: new Date(Date.now() - 7200000).toISOString(),
    remitterName: 'Ahmed Raza',
    beneficiaryName: 'Nadia Parveen',
    beneficiaryIBAN: 'PK33MEZN0012345678901',
    beneficiaryBankName: 'Meezan Bank',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 9300000).toISOString(),
        primaryRef: 'SCR-DEMO-031',
        durationMs: 1200,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 9000000).toISOString(),
        primaryRef: 'TF-DEMO-031',
        durationMs: 750,
        attempts: 1,
      },
      {
        stateCode: 'ONELINK_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 7200000).toISOString(),
        primaryRef: '1LINK-DEMO-031',
        failReasonCode: 'ONELINK_TIMEOUT',
        durationMs: 30000,
        attempts: 3,
      },
    ],
  },
  {
    id: 'TXN-DEMO-032',
    mtoName: 'RIA Money Transfer',
    mtoRef: 'MTO-DEMO-032',
    bankRef: 'BNK-DEMO-032',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '2100.00',
    currencyFCY: 'USD',
    amountPKR: '588,000',
    corridor: 'PK-US',
    purposeCode: 'MED',
    purposeText: 'Medical',
    currentState: 'ONELINK_POST',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 14400000).toISOString(),
    updatedAt: new Date(Date.now() - 10800000).toISOString(),
    remitterName: 'Carlos Martinez',
    beneficiaryName: 'Kashif Mehmood',
    beneficiaryIBAN: 'PK44HABB0098765432100',
    beneficiaryBankName: 'HBL',
    amendAllowed: false,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 14000000).toISOString(),
        primaryRef: 'SCR-DEMO-032',
        durationMs: 1000,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 13500000).toISOString(),
        primaryRef: 'TF-DEMO-032',
        durationMs: 900,
        attempts: 1,
      },
      {
        stateCode: 'ONELINK_POST',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 10800000).toISOString(),
        primaryRef: '1LINK-DEMO-032',
        failReasonCode: 'ONELINK_TIMEOUT',
        durationMs: 30000,
        attempts: 1,
      },
    ],
  },

  // ===== ADDITIONAL SCREENING TRANSACTIONS =====
  {
    id: 'TXN-DEMO-033',
    mtoName: 'InstantCash',
    mtoRef: 'MTO-DEMO-033',
    bankRef: 'BNK-DEMO-033',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '450.00',
    currencyFCY: 'USD',
    amountPKR: '126,000',
    corridor: 'PK-AE',
    purposeCode: 'SAL',
    purposeText: 'Salary',
    currentState: 'SCREENING',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 6000000).toISOString(),
    updatedAt: new Date(Date.now() - 3000000).toISOString(),
    remitterName: 'Daniel Lee',
    beneficiaryName: 'Tariq Shah',
    beneficiaryIBAN: 'PK55NBPA0000009876543',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 3000000).toISOString(),
        primaryRef: 'SCR-DEMO-033',
        failReasonCode: 'SCREENING_TIMEOUT',
        durationMs: 15000,
        attempts: 2,
      },
    ],
  },
  {
    id: 'TXN-DEMO-034',
    mtoName: 'MoneyGram',
    mtoRef: 'MTO-DEMO-034',
    bankRef: 'BNK-DEMO-034',
    branchCode: '0002',
    agentCode: 'SA002',
    payoutType: 'OTC',
    amountFCY: '670.00',
    currencyFCY: 'USD',
    amountPKR: '187,600',
    corridor: 'PK-UK',
    purposeCode: 'FAM',
    purposeText: 'Family Support',
    currentState: 'SCREENING',
    lifecycleStatus: 'IN_PROGRESS',
    createdAt: new Date(Date.now() - 3600000).toISOString(),
    updatedAt: new Date(Date.now() - 1800000).toISOString(),
    remitterName: 'Sophie Turner',
    beneficiaryName: 'Rabia Noor',
    beneficiaryIBAN: 'PK66SCBL0000001234567',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PENDING',
        timestamp: new Date(Date.now() - 1800000).toISOString(),
        primaryRef: 'SCR-DEMO-034',
        durationMs: 0,
        attempts: 1,
      },
    ],
  },

  // ===== ADDITIONAL BALANCE CHECK TRANSACTIONS =====
  {
    id: 'TXN-DEMO-035',
    mtoName: 'Western Union',
    mtoRef: 'MTO-DEMO-035',
    bankRef: 'BNK-DEMO-035',
    branchCode: '',
    agentCode: '',
    payoutType: 'FT',
    amountFCY: '1800.00',
    currencyFCY: 'USD',
    amountPKR: '504,000',
    corridor: 'PK-SA',
    purposeCode: 'EDU',
    purposeText: 'Education',
    currentState: 'BALANCE_CHECK',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 10800000).toISOString(),
    updatedAt: new Date(Date.now() - 7200000).toISOString(),
    remitterName: 'Thomas Anderson',
    beneficiaryName: 'Hamza Qureshi',
    beneficiaryIBAN: 'PK77UNIL0109000654321',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 10000000).toISOString(),
        primaryRef: 'SCR-DEMO-035',
        durationMs: 1100,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 9500000).toISOString(),
        primaryRef: 'TF-DEMO-035',
        durationMs: 800,
        attempts: 1,
      },
      {
        stateCode: 'BALANCE_CHECK',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 7200000).toISOString(),
        primaryRef: 'BC-DEMO-035',
        failReasonCode: 'INSUFFICIENT_BALANCE',
        durationMs: 500,
        attempts: 1,
      },
    ],
  },
  {
    id: 'TXN-DEMO-036',
    mtoName: 'RIA Money Transfer',
    mtoRef: 'MTO-DEMO-036',
    bankRef: 'BNK-DEMO-036',
    branchCode: '',
    agentCode: '',
    payoutType: 'IBFT',
    amountFCY: '920.00',
    currencyFCY: 'USD',
    amountPKR: '257,600',
    corridor: 'PK-US',
    purposeCode: 'MED',
    purposeText: 'Medical',
    currentState: 'BALANCE_CHECK',
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - 7200000).toISOString(),
    updatedAt: new Date(Date.now() - 5400000).toISOString(),
    remitterName: 'Patricia Johnson',
    beneficiaryName: 'Samina Bibi',
    beneficiaryIBAN: 'PK88ALFH0012345678901',
    beneficiaryBankName: 'Bank Alfalah',
    amendAllowed: true,
    cancelAllowed: true,
    cancellationAction: 'SIMPLE_CANCEL',
    amendStatus: 'NONE',
    cancelStatus: 'NONE',
    railRefs: {},
    stateHistory: [
      {
        stateCode: 'SCREENING',
        status: 'PASS',
        timestamp: new Date(Date.now() - 6800000).toISOString(),
        primaryRef: 'SCR-DEMO-036',
        durationMs: 1000,
        attempts: 1,
      },
      {
        stateCode: 'TITLE_FETCH',
        status: 'PASS',
        timestamp: new Date(Date.now() - 6400000).toISOString(),
        primaryRef: 'TF-DEMO-036',
        durationMs: 850,
        attempts: 1,
      },
      {
        stateCode: 'BALANCE_CHECK',
        status: 'FAIL',
        timestamp: new Date(Date.now() - 5400000).toISOString(),
        primaryRef: 'BC-DEMO-036',
        failReasonCode: 'BALANCE_CHECK_TIMEOUT',
        durationMs: 20000,
        attempts: 2,
      },
    ],
  },
]
